import React, {useEffect, useState} from 'react';
import {View} from 'react-native';
import DropDownPicker, {ItemType} from 'react-native-dropdown-picker';
import {useDebouncedCallback} from 'use-debounce';

import {type} from '../../../design-system/tokens';
import {GeocodedSuburb} from '../../../models/middleware';
import apiService from '../../../transport/apiService';
import {suburbKey} from '../../../utils/address';

import {
  PICKER_Z_INDEX,
  PickerSearchGlyph,
  pickerWrapperStyles,
  TEXT_INSET_STYLE,
} from './PickerSearchGlyph';
import styles from './styles';

type SuburbItemType = ItemType<string> & {suburb: GeocodedSuburb};

export const suburbToItem = (suburb: GeocodedSuburb): SuburbItemType => ({
  label: `${suburb.locality}, ${suburb.administrative_area} ${suburb.postal_code}`,
  // DropDownPicker uses `value` as the row key. It must be unique per suburb —
  // locality alone collides across states (ST AGNES QLD vs SA) and makes results
  // stack on each keystroke. See suburbKey + SuburbPicker.test.tsx.
  value: suburbKey(suburb),
  suburb,
});

type Props = {
  onSelectSuburb: (suburb: GeocodedSuburb) => void;
  initialSuburb?: {
    locality: string;
    administrative_area: string;
    postal_code: string;
  } | null;
  testID?: string;
};

const SuburbPicker = ({onSelectSuburb, initialSuburb, testID}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState<SuburbItemType[]>([]);
  const [value, setValue] = useState<string | null>(null);

  // Only initialize once on mount if initialSuburb has actual values
  useEffect(() => {
    if (initialSuburb?.locality) {
      const initialItem = suburbToItem(initialSuburb);
      setItems([initialItem]);
      setValue(initialItem.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debouncedApiCall = useDebouncedCallback(async (text: string) => {
    const suburbs = await apiService.searchSuburbs(text);
    const suburbItems = suburbs.map(suburbToItem);
    setItems(suburbItems);
  }, 500);

  const handleSearchTextChange = (text: string) => {
    debouncedApiCall(text);
  };

  const handleChangeValue = (val: string | null) => {
    if (val) {
      const selectedItem = items.find(item => item.value === val);
      if (selectedItem) {
        onSelectSuburb(selectedItem.suburb);
      }
    }
  };

  return (
    <View style={pickerWrapperStyles.wrapper}>
      <DropDownPicker
        open={isOpen}
        value={value}
        items={items}
        setOpen={setIsOpen}
        searchable
        searchPlaceholder="Search for a suburb"
        placeholder="Select a suburb"
        setValue={setValue}
        setItems={setItems}
        onChangeSearchText={handleSearchTextChange}
        onChangeValue={handleChangeValue}
        listMode="SCROLLVIEW"
        disableLocalSearch={true}
        itemSeparator={true}
        showArrowIcon={false}
        disableBorderRadius={true}
        // Closed "Select a suburb" was fontSize 20 beside 16px form fields.
        // type.button.category is the regular 16 step. styles.ts stays frozen
        // so AddressPicker is unchanged (DIS-1748 R6).
        placeholderStyle={[styles.placeholderStyle, type.button.category]}
        style={[styles.pickerStyle, TEXT_INSET_STYLE, {display: isOpen ? 'none' : 'flex'}]}
        containerStyle={styles.containerStyle}
        labelStyle={[styles.labelStyle, type.button.category]}
        searchContainerStyle={styles.searchContainerStyle}
        searchTextInputStyle={[
          styles.searchTextInputStyle,
          TEXT_INSET_STYLE,
          isOpen ? styles.searchTextInputStyleOpen : null,
        ]}
        searchPlaceholderTextColor="#BBB"
        dropDownContainerStyle={styles.dropDownContainerStyle}
        listItemContainerStyle={styles.listItemContainerStyle}
        listItemLabelStyle={styles.listItemLabelStyle}
        itemSeparatorStyle={styles.itemSeparatorStyle}
        zIndex={PICKER_Z_INDEX}
        // The library applies zIndexInverse (default 6000) when the list opens
        // upward, which covers the search glyph; keep both at PICKER_Z_INDEX.
        zIndexInverse={PICKER_Z_INDEX}
        scrollViewProps={{keyboardShouldPersistTaps: 'always'}}
        testID={testID}
      />
      <PickerSearchGlyph isOpen={isOpen} testID={`${testID ?? 'suburb-picker'}-search-icon`} />
    </View>
  );
};

export default SuburbPicker;
